import { ArrowRight, Check, Clock, Users } from 'lucide-react';
import { AvatarCircle } from '@/components/avatar-circle';
import { liveCoverUrl } from '@/lib/live-covers';
import type { MyPrivateLive } from '@/lib/private-live';
import {
  GlassChip,
  LiveCardMeta,
  LiveCoverCard,
  liveCtaClass,
} from '@/features/live-ui/live-cover-card';
import {
  levelSubjectChip,
  participantsLabel,
  privateLiveCardView,
} from '@/features/live-ui/live-card-model';
import { useNow } from '@/features/live-ui/use-now';
import { formatRelativeDate } from './format';

/**
 * Carte Feed d'un défi live privé (migrations 0650, 0658) : la cover du défi,
 * l'état (LIVE, défi privé, terminé), le niveau, le titre, les participants et
 * le départ, puis l'organisateur et le bouton (salon, jeu ou classement).
 */
export function PrivateLiveCard({
  data,
  size = 'lg',
  className,
}: {
  data: MyPrivateLive;
  size?: 'lg' | 'md';
  className?: string;
}) {
  const now = useNow();
  const view = privateLiveCardView(data, now);
  const hostName = data.host?.display_name ?? 'Un contact';

  return (
    <LiveCoverCard
      testId="private-live-card"
      size={size}
      className={className}
      coverUrl={liveCoverUrl({ path: data.cover_path, seed: data.id })}
      badge={view.badge}
      chip={levelSubjectChip(data.level, data.subject)}
      corner={<GlassChip className="font-medium">{formatRelativeDate(data.invited_at)}</GlassChip>}
      title={data.title ?? 'Défi privé'}
    >
      <LiveCardMeta
        className="mt-2"
        items={[
          { icon: Users, text: participantsLabel(data.registered) },
          { icon: Clock, text: view.timeLabel },
          ...(data.joined && !data.hosted && !view.finished
            ? [{ icon: Check, text: 'Tu participes' }]
            : []),
        ]}
      />
      <div className="mt-3 flex items-center gap-2.5">
        <AvatarCircle
          name={hostName}
          src={data.host?.avatar_url}
          size="sm"
          className="ring-2 ring-[rgba(255,255,255,0.7)]"
        />
        <p className="min-w-0 flex-1 text-sm leading-snug text-white/90">
          <span className="font-semibold text-white">{view.intro}</span>
          <span className="block truncate text-white/70">{view.summary}</span>
        </p>
      </div>
      <a href={`/defi-live/${data.join_code}`} className={liveCtaClass(view.cta.tone, 'mt-4')}>
        {view.cta.label}
        {!view.finished && <ArrowRight className="h-5 w-5" aria-hidden />}
      </a>
    </LiveCoverCard>
  );
}
